import { setDepartureLine } from './multipleBirth';
import { english } from 'src/i18n/language';

/**
 * What a Delete Person actually takes with it.
 *
 * The confirmation dialog has always named the person and made you type their UPN
 * to proceed. What it never said is what happens to everyone attached to them, and
 * the answer is more surprising than "nothing": **no PERSON cascades**. Children are
 * not deleted, they are ORPHANED — `fatherId`/`motherId` go null and they drop out
 * of `childrenIds`, both client-side (`stripMemberReferencesToPid`) and on the
 * server. On a three-generation pedigree that leaves a parentless sibship floating
 * mid-chart, with no warning, from a menu item that reads like it removes one
 * record.
 *
 * This module computes that list so the dialog can state it. TGCLOUD-1644.
 *
 * WHAT EARNS A LINE, and what does not.
 *
 * A warning that fires on every deletion stops being read. Browser QA found the
 * first version causing hesitation on routine deletions, so the bar is now
 * explicit: **a consequence is listed only when it could leave the pedigree in a
 * state the clinician would not have chosen** — a section orphaned, a union split,
 * or clinical information lost that cannot be recovered from what remains.
 *
 * By that bar:
 * - **Orphaned children** — listed. This is the whole reason the block exists.
 * - **Unions** — listed. Removing one partner can split a pedigree in two.
 * - **Twin links** — listed. The survivor stops being drawn as a twin, and
 *   zygosity is genetic information that cannot be re-derived from the chart.
 * - **Sibling links** — NOT listed. Siblinghood between the survivors is
 *   unchanged: they still share the parents they shared before, and the drawing
 *   does not move. Nothing is lost, so saying so was pure noise.
 * - **Their clinical records** — listed ONLY when some exist, with counts. See
 *   below. (Records DO cascade, since TGCLOUD-1848; people do not.)
 */

/**
 * The records line, which needs COUNTS rather than a hunch (TGCLOUD-1848).
 *
 * Delete Person deletes the person's clinical records with them — notes of every type,
 * pregnancies, risk results, letters, care pathways — and none of that is loaded with the
 * pedigree, so the dialog asks: `GET /pedigree/:user_id/delete-impact/:id` is a DRY RUN of the
 * same cascade the delete runs, so these numbers are the delete's numbers. Like the care-pathway
 * line this replaces, it is shown ONLY when there is something to lose: most deletions are of
 * people with no records at all, and a warning about records that do not exist is undue
 * hesitation (browser QA, TGCLOUD-1644).
 */
// Every line is built for the screen: the builders take the caller's `t`,
// English by default, and a count is ONE key per form ('{{count}} test' and
// '{{count}} tests'), never an English ending.
const plural = (n, one, many, t) => t(n === 1 ? one : many, { count: n });

const NOTE_TYPE_LABELS = [
  ['Test', '{{count}} test', '{{count}} tests'],
  ['Referral', '{{count}} referral', '{{count}} referrals'],
  ['Appointment', '{{count}} appointment', '{{count}} appointments'],
  ['Clinical Note', '{{count}} clinical note', '{{count}} clinical notes'],
  ['Consent', '{{count}} consent', '{{count}} consents'],
  ['Sample', '{{count}} sample', '{{count}} samples'],
  ['Treatment', '{{count}} treatment', '{{count}} treatments'],
  [
    'Tissue Pathology',
    '{{count}} tissue pathology record',
    '{{count}} tissue pathology records'
  ],
  ['Contact', '{{count}} contact record', '{{count}} contact records'],
  ['Process', '{{count}} process record', '{{count}} process records'],
  ['Address', '{{count}} address', '{{count}} addresses']
];

export const recordsNote = (impact, t = english) => {
  if (!impact || typeof impact !== 'object') return null;
  const byType = impact.notesByType || {};
  const parts = [];
  const known = new Set();
  for (const [type, one, many] of NOTE_TYPE_LABELS) {
    known.add(type);
    if (byType[type] > 0) parts.push(plural(byType[type], one, many, t));
  }
  const other = Object.entries(byType)
    .filter(([type, n]) => !known.has(type) && n > 0)
    .reduce((sum, [, n]) => sum + n, 0);
  if (other)
    parts.push(
      plural(
        other,
        '{{count}} other clinical record',
        '{{count}} other clinical records',
        t
      )
    );
  if (impact.pregnancies > 0)
    parts.push(
      plural(
        impact.pregnancies,
        '{{count}} pregnancy',
        '{{count}} pregnancies',
        t
      )
    );
  if (impact.riskRecords > 0)
    parts.push(
      plural(
        impact.riskRecords,
        '{{count}} risk result',
        '{{count}} risk results',
        t
      )
    );
  if (impact.variantReclassifications > 0)
    parts.push(
      plural(
        impact.variantReclassifications,
        '{{count}} variant reclassification',
        '{{count}} variant reclassifications',
        t
      )
    );
  if (impact.letters > 0)
    parts.push(
      plural(impact.letters, '{{count}} letter', '{{count}} letters', t)
    );
  if (impact.pathways > 0)
    parts.push(
      plural(
        impact.pathways,
        '{{count}} care pathway',
        '{{count}} care pathways',
        t
      )
    );
  return parts.length
    ? t('Their records will also be deleted: {{records}}.', {
        records: parts.join(', ')
      })
    : null;
};

/**
 * Fallback wording for when the counts could not be fetched. Deliberately still
 * shown: failing to warn about a real deletion is worse than a moment's pause,
 * and this path is rare. This and the two notes below are English, translated
 * where they are shown.
 */
export const RECORDS_NOTE_UNKNOWN =
  'Any clinical records for this person — tests, referrals, appointments, pregnancies, care pathways — will also be deleted.';

/**
 * The family's original proband. Deleting them re-roots the family: THEIR records go, the
 * family's move to the new root. There is no dry run for that path, so it is stated plainly.
 */
export const ROOT_RECORDS_NOTE =
  "Their own clinical records will also be deleted. The family's records move to the new family root.";

/** The reassurance that stops the orphan lines reading as "the children go too". */
export const CHILDREN_KEPT_NOTE =
  'Their children stay on the pedigree and are not deleted.';

const displayName = (m, t) =>
  [m?.firstName, m?.lastName].filter(Boolean).join(' ') ||
  (m?.upn != null
    ? t('UPN {{upn}}', { upn: m.upn })
    : t('Person {{pid}}', { pid: m?.pid }));

/**
 * @param {object} node    the person about to be deleted
 * @param {Array}  members the member records the chart is drawn from
 * @param {Function} [t]   the caller's t(), English by default
 * @returns {{ lines: string[], childCount: number, spouseNames: string[],
 *            twinCount: number }}
 *
 * `lines` is ready to render, already filtered of anything that would read "0".
 * A dialog that always shows five bullets, three of them zero, teaches people to
 * skip it — so a leaf node gets no bullets at all beyond the pathway note.
 */
export const deleteConsequences = (node, members, t = english) => {
  const pid = node?.pid;
  const list = Array.isArray(members) ? members : [];

  const empty = {
    lines: [],
    childCount: 0,
    spouseNames: [],
    twinCount: 0
  };
  if (pid == null) return empty;

  // Children are found from the CHILD's own back-pointers rather than the
  // parent's childrenIds: the back-pointers are what the delete actually nulls,
  // and a childrenIds array can carry a stale pid (see analyzeMemberEdges).
  const children = list.filter(
    (m) => m && (m.fatherId === pid || m.motherId === pid)
  );

  // A child who would be left with NO parent at all is the worse case, and worth
  // separating: losing one of two parents leaves the descent line drawn.
  const fullyOrphaned = children.filter((c) => {
    const other = c.fatherId === pid ? c.motherId : c.fatherId;
    return other == null || !list.some((m) => m.pid === other);
  });

  const spouseNames = (
    list.find((m) => m.pid === pid)?.spouseIds ||
    node?.spouseIds ||
    []
  )
    .map((sid) => list.find((m) => m.pid === sid))
    .filter(Boolean)
    .map((m) => displayName(m, t));

  const self = list.find((m) => m.pid === pid) || node || {};
  // NOTE: siblingIds is deliberately NOT counted — see the header. The survivors
  // remain each other's siblings through the parents they already share.
  const twinCount = (self.twinRelations || []).filter((r) =>
    list.some((m) => m.pid === r?.withPid)
  ).length;

  const lines = [];

  if (children.length) {
    const isMother = self.gender === 'female';
    lines.push(
      fullyOrphaned.length === children.length
        ? plural(
            children.length,
            'leave {{count}} child with no recorded parent',
            'leave {{count}} children with no recorded parent',
            t
          )
        : isMother
        ? plural(
            children.length,
            'leave {{count}} child without a recorded mother',
            'leave {{count}} children without a recorded mother',
            t
          )
        : plural(
            children.length,
            'leave {{count}} child without a recorded father',
            'leave {{count}} children without a recorded father',
            t
          )
    );
  }

  if (spouseNames.length) {
    lines.push(
      spouseNames.length === 1
        ? t('remove their union with {{name}}', { name: spouseNames[0] })
        : t('remove {{count}} unions — {{names}}', {
            count: spouseNames.length,
            names: spouseNames.join(', ')
          })
    );
  }

  if (twinCount) {
    // Above a pair the LINK COUNT is not the consequence — what survives is. Deleting
    // one of three co-multiples leaves two twins, not three singletons, and the dialog
    // should say so (TGCLOUD-1692). A pair keeps the existing wording: there is no
    // remainder to describe, and the count is not misleading at that size.
    lines.push(
      setDepartureLine(twinCount + 1, t) ||
        plural(
          twinCount,
          'remove {{count}} twin link',
          'remove {{count}} twin links',
          t
        )
    );
  }

  return {
    lines,
    childCount: children.length,
    spouseNames,
    twinCount
  };
};
